import { useState, type FormEvent } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

export function Login() {
  const { user, tokenReady, signIn, signInWithProvider, bootstrapError } =
    useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [ssoBusy, setSsoBusy] = useState<'google' | 'microsoft' | null>(null);

  if (user && tokenReady) {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await signIn(email, password);
    } catch {
      // Enumeration-safe: one generic message for every credential
      // failure (mirrors the API's generic 401 doctrine).
      setError('Sign-in failed. Check your email and password.');
    } finally {
      setSubmitting(false);
    }
  };

  const onProviderSignIn = async (provider: 'google' | 'microsoft') => {
    setError(null);
    setSsoBusy(provider);
    try {
      await signInWithProvider(provider);
    } catch {
      setError('Sign-in failed. Try again or use email and password.');
    } finally {
      setSsoBusy(null);
    }
  };

  return (
    <div className="login-page">
      <form className="login-card" onSubmit={onSubmit} noValidate>
        <h1 className="login-title">Detect</h1>
        <p className="login-subtitle">Sign in with your Meshtek account</p>
        {(error || bootstrapError) && (
          <div className="error-state" role="alert">
            {error ?? bootstrapError}
          </div>
        )}
        <button
          type="button"
          className="btn btn-secondary btn-sso"
          disabled={ssoBusy !== null}
          aria-label="Continue with Google"
          onClick={() => onProviderSignIn('google')}
        >
          <svg aria-hidden="true" focusable="false" viewBox="0 0 48 48">
            <path
              fill="#FFC107"
              d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8a12 12 0 1 1 7.9-21l5.7-5.7A20 20 0 1 0 24 44a20 20 0 0 0 19.6-23.5z"
            />
            <path
              fill="#FF3D00"
              d="m6.3 14.7 6.6 4.8A12 12 0 0 1 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7A20 20 0 0 0 6.3 14.7z"
            />
            <path
              fill="#4CAF50"
              d="M24 44a20 20 0 0 0 13.4-5.2l-6.2-5.2a12 12 0 0 1-7.2 2.4 12 12 0 0 1-11.3-8l-6.5 5A20 20 0 0 0 24 44z"
            />
            <path
              fill="#1976D2"
              d="M43.6 20.5H42V20H24v8h11.3a12 12 0 0 1-4.1 5.6l6.2 5.2c-.4.4 6.6-4.8 6.6-14.8 0-1.3-.1-2.3-.4-3.5z"
            />
          </svg>
          {ssoBusy === 'google' ? 'Signing in…' : 'Continue with Google'}
        </button>
        <button
          type="button"
          className="btn btn-secondary btn-sso"
          disabled={ssoBusy !== null}
          aria-label="Continue with Microsoft"
          onClick={() => onProviderSignIn('microsoft')}
        >
          <svg aria-hidden="true" focusable="false" viewBox="0 0 23 23">
            <path fill="#f25022" d="M1 1h10v10H1z" />
            <path fill="#7fba00" d="M12 1h10v10H12z" />
            <path fill="#00a4ef" d="M1 12h10v10H1z" />
            <path fill="#ffb900" d="M12 12h10v10H12z" />
          </svg>
          {ssoBusy === 'microsoft' ? 'Signing in…' : 'Continue with Microsoft'}
        </button>
        <div className="login-divider" role="separator">
          or
        </div>
        <div className="form-field">
          <label htmlFor="login-email">Email</label>
          <input
            id="login-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="form-field">
          <label htmlFor="login-password">Password</label>
          <input
            id="login-password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
